// useProductionFlow — the single selector for the active packhouse's
// production flow. Reads packhouse_config.production_flow (mig 607) for the
// active tenant — the row is per-tenant + RLS-scoped, so .maybeSingle() returns
// this tenant's config. Same shape as useLetterhead().
//
// Flow-specific stage shells (src/modules/packhouse/stages/*) and the few
// flow-aware sections inside shared pages branch on this. It defaults to
// 'CITRUS' while loading or when there's no packhouse_config row, so the
// citrus path is the safe fallback everywhere and existing behaviour is
// unchanged until a packhouse is explicitly set to another flow.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useActiveTenant } from '@/lib/useActiveTenant'

// CITRUS + BLUEBERRY are the in-code flows; any other string is a flow defined
// purely as a commodity_templates row (P4/P2 — config, not code). The
// `(string & {})` keeps literal autocomplete for the known ones while admitting
// arbitrary DB-driven flow keys. Resolvers (useFlowManifest / useFlowPipeline)
// fall back to the in-code map then a safe default for an unknown key.
export type ProductionFlow = 'CITRUS' | 'BLUEBERRY' | (string & {})

// State-aware variant for STAGE SHELLS that swap whole pages: `resolved` is
// false only while the flow is genuinely unknown (tenant or config still
// loading, no cached value). In-page capability gates should keep using the
// plain useProductionFlow() — its instant CITRUS default is the right
// behaviour for a section toggle, but the wrong one for mounting an entire
// citrus page (query spray + interactable flash) on a blueberry tenant.
//
// `state` exposes the underlying query's render states. Shells MUST branch on
// it while unresolved: `resolved: data !== undefined` is false for error and
// paused too, and a bare `if (!resolved) return <spinner/>` was a route-level
// forever-spinner on /deliveries + /deliveries/report — the idle-wake audit's
// UI-2 (Critical). Render <QueryStateGate query={state}/> instead of a raw
// spinner.
export function useProductionFlowState(): {
  flow: ProductionFlow
  resolved: boolean
  state: {
    isPending: boolean; isFetching: boolean; isError: boolean; isPaused: boolean
    error: unknown; refetch: () => unknown
  }
} {
  const flow = useProductionFlow()
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null
  const { data, isPending, isFetching, isError, isPaused, error, refetch } = useQuery<ProductionFlow>({
    queryKey: ['production-flow', tenantId],
    staleTime: 5 * 60_000,
    enabled: tenantId !== null,
    queryFn: flowQueryFn(tenantId),
  })
  return {
    flow,
    resolved: data !== undefined,
    state: { isPending, isFetching, isError, isPaused, error, refetch },
  }
}

function flowQueryFn(tenantId: string | null) {
  return async (): Promise<ProductionFlow> => {
    // Scope to the active tenant explicitly. packhouse_config is UNIQUE
    // (tenant_id) (mig 433), so this is exactly 0/1 row. Without the filter a
    // session that can see >1 row (platform-admin / support-view) makes
    // .maybeSingle() throw "multiple rows", which falls through to the CITRUS
    // default — silently showing the citrus flow on a non-citrus packhouse.
    const { data, error } = await supabase
      .from('packhouse_config')
      .select('production_flow')
      .eq('tenant_id', tenantId)
      .maybeSingle()
    if (error) throw error
    return ((data?.production_flow as ProductionFlow | undefined) ?? 'CITRUS')
  }
}

export function useProductionFlow(): ProductionFlow {
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null

  const { data } = useQuery<ProductionFlow>({
    queryKey: ['production-flow', tenantId],
    staleTime: 5 * 60_000,
    enabled: tenantId !== null,
    queryFn: flowQueryFn(tenantId),
  })

  return data ?? 'CITRUS'
}
